/* eslint-disable react-refresh/only-export-components */
import type {ReactElement} from 'react';
import {createElement, useContext} from 'react';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {useComponentAttribute, useStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import type {EmailMakerControl, EmailMakerControlProps} from '../../right-panel-registry.types';
import {createDescriptorControl} from '../create-descriptor-control';
import type {ConditionalControlDescriptor} from './conditional-control.types';
import {ConditionalGatingSource} from './conditional-control.types';

interface ConditionalControlInnerProps extends EmailMakerControlProps {
  descriptor: ConditionalControlDescriptor;
}

/**
 * Self-gates on the live value of the gating style property or attribute instead of a parent-driven `isVisible`: a plain
 * edit does not re-render the parent panel, so a predicate there would only update on re-selection.
 * The gating value is passed into the predicate so the React Compiler re-evaluates it when the value changes.
 */
function ConditionalControlInner({component, descriptor}: ConditionalControlInnerProps): ReactElement | null {
  const {editor} = useContext(emailMakerContext);

  const isAttributeGated = descriptor.gatingSource === ConditionalGatingSource.ATTRIBUTE;
  const gatingStyleValue = useStyleProperty(editor, component, isAttributeGated ? '' : descriptor.gatingKey);
  const gatingAttributeValue = useComponentAttribute(component, isAttributeGated ? descriptor.gatingKey : '');
  const gatingValue = isAttributeGated ? gatingAttributeValue : gatingStyleValue;

  if (!descriptor.predicate(component, gatingValue)) {
    return null;
  }

  return createElement(descriptor.control, {component});
}

export function makeConditionalControl(descriptor: ConditionalControlDescriptor): EmailMakerControl {
  return createDescriptorControl(ConditionalControlInner, descriptor);
}
